import { TableCell } from 'ui/tableCell/TableCell';
import { DateCell } from 'ui/tableCell/variants/DateCell';
import { GhostButton } from 'ui/button';
import { FlexSpacer } from 'ui/flexSpacer';
import { Badge, BADGE_VARIANTS } from 'ui/badge';
import type {
  TemplateHistoryTableProps,
  TemplateHistoryTemplate,
  TemplateHistoryMetadata,
  TemplateHistoryTableData,
  TemplateHistoryModalState,
} from '../../types';
import { Redo16 } from 'ui/icons/aurora/16px/Redo16';

export const configureTemplateHistoryTableColumns = (
  templateContentIds: TemplateHistoryTableProps['templateContentIds'] = [],
  templateId: TemplateHistoryTemplate['id'],
  onClick: (data: TemplateHistoryModalState) => void,
  whatsAppContentId?: TemplateHistoryTemplate['whatsAppContentId'],
): TemplateHistoryTableData[] => {
  const defaultColumns: TemplateHistoryTableData[] = [
    {
      name: 'ID',
      align: 'flex-start',
      value: 'id',
      sortable: false,
      RowCell: ({ row }) => <TableCell>{row.id}</TableCell>,
    },
    {
      name: 'Edited by',
      align: 'flex-start',
      value: 'creator',
      sortable: false,
      RowCell: ({ row }) => <TableCell>{row.creatorFullName}</TableCell>,
    },
    {
      name: 'Last Modified',
      align: 'flex-start',
      value: 'createdAt',
      sortable: false,
      RowCell: ({ row }) => {
        return <DateCell isRelative={false} timestamp={row.createdAt} isUnderlined={false} />;
      },
    },
    {
      name: 'Locale',
      align: 'flex-start',
      value: 'locale',
      sortable: false,
      RowCell: ({ row }) => (
        <TableCell>
          <Badge variant={BADGE_VARIANTS.DRAFT}>{row.locale}</Badge>
        </TableCell>
      ),
    },
  ];

  const restoreActionColumn: TemplateHistoryTableData = {
    name: '',
    align: 'flex-end',
    value: '',
    sortable: false,
    RowCell: ({ row }) => {
      const isCurrentContentVersion = templateContentIds.includes(row.id);
      return (
        <TableCell data-test="table-history-restore-content-action-cell">
          <FlexSpacer justifyContent="flex-end">
            {templateContentIds.includes(row.id) ? (
              <Badge
                variant={BADGE_VARIANTS.RECURRING}
                data-test="template-history-table-current-version-badge"
              >
                Current version
              </Badge>
            ) : (
              <GhostButton
                data-test="table-history-restore-content-action-button"
                isDisabled={isCurrentContentVersion}
                onClick={() => {
                  onClick({ action: 'restoreTemplateModal', newContent: row, templateId });
                }}
              >
                <Redo16 /> Restore
              </GhostButton>
            )}
          </FlexSpacer>
        </TableCell>
      );
    },
  };

  /**
   * The "restore content" action will not work on whatsapp templates (see https://github.com/Iterable/Iterable/pull/23575).
   * So we will not show the action button column if the template is a whatsapp template.
   * Reach out to the channels team for more information if needed.
   */
  if (!!whatsAppContentId) {
    return defaultColumns;
  }

  return [...defaultColumns, restoreActionColumn];
};

/**
 * filterTemplateHistoryRows
 * used to filter enabled accounts by id, creator, or locale in the TemplateHistory client paginated table
 */
export const filterTemplateHistoryRows = (row: TemplateHistoryMetadata, filter: string) => {
  return (
    row.id.toString().includes(filter.toLowerCase()) ||
    row.creatorFullName.toLowerCase().includes(filter.toLowerCase()) ||
    row.locale.toLowerCase().includes(filter)
  );
};

export const getTemplateDiffUrl = (
  templateId: TemplateHistoryTemplate['id'],
  contentIdLeft: TemplateHistoryMetadata['id'],
  contentIdRight: TemplateHistoryMetadata['id'],
) => {
  return `/templates/diff?templateId=${templateId}&contentIdLeft=${contentIdLeft}&contentIdRight=${contentIdRight}`;
};
